import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import axiosInstance from '../helpers/axiosInstance';
import "./vehicleEntry.css";
import { socket } from "../helpers/socketInstance";


// Add this custom hook right after your imports
const useClickOutside = (ref, callback) => {
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (ref.current && !ref.current.contains(event.target)) {
                callback();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [ref, callback]);
};

const VehicleEntry = () => {
    const { state } = useLocation();
    const [vehicleData, setVehicleData] = useState(null);
    const [vehicleNumber, setVehicleNumber] = useState("");
    const [isManual, setIsManual] = useState(false);
    const [isNewVehicle, setIsNewVehicle] = useState(false);
    const [source, setSource] = useState("alpr");
    const clearTimerRef = useRef(null);
    const intervalRef = useRef(null);
    const [remainingTime, setRemainingTime] = useState(null);
    const startCountdown = () => {
        setRemainingTime(60);
        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = setInterval(() => {
            setRemainingTime((prev) => {
                if (prev <= 1) {
                    clearInterval(intervalRef.current);
                    return null;
                }
                return prev - 1;
            });
        }, 1000);
    };
    const [showFormModal, setShowFormModal] = useState(false);
    const [vendorCode, setVendorCode] = useState("");
    const [trip, setTrip] = useState("");
    const [driverName, setDriverName] = useState("");
    const [driverContact, setDriverContact] = useState("");
    const [vendorList, setVendorList] = useState([]);
    const [vendorPage, setVendorPage] = useState(1);
    const [loadingVendors, setLoadingVendors] = useState(false);
    const [vendorSearch, setVendorSearch] = useState("");
    const [hasMore, setHasMore] = useState(true);
    const vendorListRef = useRef(null);
    const [isVendorDropdownOpen, setIsVendorDropdownOpen] = useState(false);
    const [tripList, setTripList] = useState([]);
    const [loadingTrips, setLoadingTrips] = useState(false);
    const [isTripDropdownOpen, setIsTripDropdownOpen] = useState(false);
    const [openedByUser, setOpenedByUser] = useState(false);
    const tripListRef = useRef(null);
    const [showWarning, setShowWarning] = useState(false);
    const [isVehicleRegistered, setIsVehicleRegistered] = useState(false);
    const [recommendedTrip, setRecommendedTrip] = useState(null);
    const [recommendedTripId, setRecommendedTripId] = useState('');

    useClickOutside(vendorListRef, () => {
        setIsVendorDropdownOpen(false);
    });

    // Check localStorage for manual mode on component mount
    useEffect(() => {
        const manualActive = localStorage.getItem('manualEntryActive') === 'true';
        setIsManual(manualActive);
    }, []);

    const isManualActive = () => localStorage.getItem('manualEntryActive') === 'true';

    useEffect(() => {
        const room = localStorage.getItem("VehicleEntry-Room");
        if (room) {
            console.log("🔌 Joining room:", room);
            socket.emit("joinRoom", room);
        }

        const handleVehicleData = (data) => {
            console.log("🚗 Received vehicle data:", data);
            if (isManualActive()) {
                console.log("Ignoring vehicle data because manual mode is active");
                return;
            }
            setVehicleData(data);
            setVehicleNumber(data.vehicleNumber || "");
            setSource(data.source || "alpr");
            setIsNewVehicle(true);
            getSourceLabel()
            getSourceColor();
            if (data.destinationStatus === "not_scheduled" && openedByUser) {
                setShowFormModal(true);
            }

            setTimeout(() => setIsNewVehicle(false), 1500);

            // 🧹 Clear old timer if exists
            if (clearTimerRef.current) {
                clearTimeout(clearTimerRef.current);
                console.log("🧼 Cleared previous timer");
            }

            // ⏱️ Set new timer for auto-clear
            clearTimerRef.current = setTimeout(() => {
                console.log("🧼 Auto-clearing vehicle data after 2 min");
                setVehicleData(null);
                setVehicleNumber("");
                setSource("alpr");
            }, 60000); // 1 minutes
            startCountdown();
        };


        socket.on("vehicle-entry", handleVehicleData);

        return () => {
            socket.off("vehicle-entry", handleVehicleData);
            if (clearTimerRef.current) {
                clearTimeout(clearTimerRef.current);
                clearInterval(intervalRef.current);
            }
        };
    }, []);
    useEffect(() => {
        if (state?.vehicleData) {
            console.log("🚗 Got vehicleData from state:", state.vehicleData);
            if (isManualActive()) {
                console.log("Ignoring vehicle data from state because manual mode is active");
                return;
            }
            setVehicleData(state.vehicleData);
            setVehicleNumber(state.vehicleData.vehicleNumber || "");
            console.log("source", state.vehicleData.source);
            setSource(state.vehicleData.source || "alpr");
            setIsNewVehicle(true);
            getSourceLabel()
            getSourceColor();
            console.log("State dara ", state.vehicleData.destinationStatus)


            setTimeout(() => setIsNewVehicle(false), 1500);


            if (clearTimerRef.current) {
                clearTimeout(clearTimerRef.current);

            }

            clearTimerRef.current = setTimeout(() => {
                setVehicleData(null);
                setVehicleNumber("");
                setSource("alpr");
                setRemainingTime(null);
            }, 60000);
            startCountdown();
        }
    }, [state]);
    const fetchTrips = async (vendorCode) => {
        try {
            setLoadingTrips(true);

            const res = await axiosInstance.get(`/api/trip/vendor-based-tripId/${vendorCode}`);

            if (res?.data?.data) {
                const formattedTrips = res.data.data.map(trip => ({
                    id: trip.id,
                    trip_id: trip.route_name,
                    vendor_code: trip.vendor_code
                }));
                setTripList(formattedTrips);
            } else {
                setTripList([]);
            }
        } catch (error) {
            console.error("Error fetching trips:", error);
            setTripList([]);
        } finally {
            setLoadingTrips(false);
        }
    };

    const handleManualToggle = () => {
        setIsManual(!isManual);
        if (!isManual) {
            localStorage.setItem('manualEntryActive', 'true');
            setVehicleData(null);
            setSource("manual");
        } else {
            localStorage.setItem('manualEntryActive', 'false');
        }
    };
    const fetchVendor = async (search = "", isNewSearch = false) => {
        try {
            setLoadingVendors(true);
            const currentPage = isNewSearch ? 1 : vendorPage;

            const res = await axiosInstance.get(
                `/api/vendor/vendors?number=${search}&limit=30&page=${currentPage}`
            );

            if (res?.data?.vendors) {
                if (isNewSearch) {
                    setVendorList(res.data.vendors);
                } else {
                    setVendorList(prev => [...prev, ...res.data.vendors]);
                }
                setHasMore(res.data.vendors.length === 30);
            } else {
                if (isNewSearch) {
                    setVendorList([]);
                }
                setHasMore(false);
            }
        } catch (error) {
            console.error("Error fetching vendors:", error);
            setVendorList([]);
            setHasMore(false);
        } finally {
            setLoadingVendors(false);
        }
    };
    const handleVendorScroll = (e) => {
        const { scrollTop, clientHeight, scrollHeight } = e.target;
        if (scrollHeight - scrollTop <= clientHeight * 1.5 && !loadingVendors && hasMore) {
            setVendorPage(prev => prev + 1);
        }
    };
    const handleVendorSearch = (e) => {
        const searchValue = e.target.value;
        setVendorSearch(searchValue);
        setVendorPage(1);
        fetchVendor(searchValue, true);
    };
    useEffect(() => {
        // Fetch vendors when modal opens or vendorPage changes
        if (showFormModal && hasMore) {
            fetchVendor(vendorSearch);
        }
    }, [showFormModal, vendorPage]);

    const handleUpdate = async () => {
        try {
            const selectedLocation = JSON.parse(localStorage.getItem("selectedLocation"));

            if (!selectedLocation || !selectedLocation.id || !selectedLocation.module?.module_name) {
                Swal.fire({
                    icon: "error",
                    title: "Location Not Selected",
                    text: "Please select a valid location before submitting.",
                });
                return;
            }

            const locationId = selectedLocation.id;
            const moduleName = selectedLocation.module.module_name;

            const payload = {
                Plate: vehicleNumber,
                ContextImage: "Not Available"
            };

            const response = await axiosInstance.post(
                `/api/auth/alpr?source=manual&gateNumber=${locationId}&module=${moduleName}`,
                payload
            );

            console.log("✅ ALPR request sent. Response:", response.data);

            // ✅ Check if backend actually accepted vehicle
            if (response.data.success) {
                Swal.fire({
                    icon: "success",
                    title: "Vehicle Submitted",
                    text: response.data.message || `Vehicle number ${vehicleNumber} sent for processing`,
                });
                localStorage.setItem('manualEntryActive', 'false');
                setIsManual(false);
            } else {
                Swal.fire({
                    icon: "warning",
                    title: "Vehicle Not Processed",
                    text: response.data.message || "Vehicle not registered",
                });
            }

        } catch (error) {
            console.error("❌ Error sending vehicle data:", error);
            Swal.fire({
                icon: "error",
                title: "Submission Failed",
                text: error.response?.data?.message || error.message,
            });
        }
    };

    //function for check the vehicle is registered or not
    const checkvehicleRegistration = async (vehicleNumber) => {
        try {
            // const selectedLocation = JSON.parse(localStorage.getItem("selectedLocation"));

            // if (!selectedLocation || !selectedLocation.id || !selectedLocation.module?.module_name) {
            //   setIsVehicleRegistered(false);
            //   return;
            // }



            const response = await axiosInstance.get(
                `/api/auth/registervehiclecheck/${vehicleNumber}`,

            );

            console.log("Vehicle registration response:", response.data);
            if (response.data.message == 'Vehicle not registered') {
                setIsVehicleRegistered(false);
            }
            else {
                setIsVehicleRegistered(true);

            }
            return response.data;

        } catch (error) {
            console.error(
                "Error sending vehicle registration:",
                error.response ? error.response.data : error.message
            );
            setIsVehicleRegistered(false);
        }
    };

    //function for call the alpr api



    const formatVehicleNumber = (number) =>
        number.replace(/(\w{2})(\d{2})(\w{2})(\d{4})/, "$1 $2 $3 $4");

    const getSourceLabel = () => {
        switch (source) {
            case "manual":
                return "✍️ Manual";
            case "rfid":
                return "📡 RFID";
            case "alpr":
            default:
                return "🔍 ALPR";
        }
    };

    //calling the function


    const getSourceColor = () => {
        switch (source) {
            case "manual":
                return "#198754";
            case "rfid":
                return "#6c757d";
            case "alpr":
            default:
                return "#0d6efd";
        }
    };
    // Add this useEffect after your existing useEffect hooks
    useEffect(() => {
        if (vendorCode) {
            setTrip(''); // Clear previous trip selection
            fetchTrips(vendorCode); // Fetch all trips for the vendor
        }
    }, [vendorCode]);

    // Add this near the top where other useClickOutside hooks are defined
    useClickOutside(tripListRef, () => {
        setIsTripDropdownOpen(false);
    });
    // Add this function before the return statement

    const handleSubmit = async () => {
        try {
            // Validate required fields

            // const selectedLocation = JSON.parse(localStorage.getItem("selectedLocation"));
            // console.log("the selectedLocation is",selectedLocation);

            // if (!selectedLocation || !selectedLocation.id || !selectedLocation.module?.module_name) {
            //     Swal.fire({
            //         icon: "error",
            //         title: "Location Not Selected",
            //         text: "Please select a valid location before submitting.",
            //     });
            //     return;
            // }
            // const locationId = selectedLocation.id;
            // if (!vendorCode || !recommendedTrip || !driverName || !driverContact) {
            //     Swal.fire({
            //         icon: 'error',
            //         title: 'Missing Information',
            //         text: 'Please fill in all required fields'
            //     });
            //     return;
            // }
            // const now = new Date();
            // const entryTime = now.toLocaleTimeString('en-GB', { hour12: false });

            // const payload = {
            //     locationId,
            //     truck_number: vehicleNumber,
            //     vendor_code: vendorCode,
            //     trip: recommendedTrip,
            //     trip_id: recommendedTripId,
            //     driver_name: driverName,
            //     driver_mobile_number: driverContact,
            //     entry_time: entryTime,
            //     module: selectedLocation.module.module_name
            // };
            // console.log("THE PAYLOAD IS",payload);

            // const response = await axiosInstance.post('/api/scheduled/truck-schedules', payload);

            const payload = {
                truck_number: vehicleNumber,
                vendor_code: vendorCode,
                driver_name: driverName,
                driver_mobile_number: driverContact,
            };
            const response = await axiosInstance.post('/api/scheduled/addvehicle-info', payload);

            if (response.status === 200) {
                Swal.fire({
                    icon: 'success',
                    title: 'success',
                    text: 'Vehicle info added successfully'
                });
                handleSubmitRegisteredVehicle(vehicleNumber);
                // Reset form fields
                setVendorCode('');
                setRecommendedTrip(null);
                setDriverName('');
                setDriverContact('');
                setVendorSearch('');
                setVehicleNumber('')

                // Close modal
                setShowFormModal(false);
                setVehicleData(response.data.data)
            }
            else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to schedule vehicle entry'
                });
            }
        } catch (error) {
            console.error('Error scheduling vehicle entry:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.message || 'Failed to schedule vehicle entry'
            });
        }
    };

    //handle submit for registered Vehicle
    const handleSubmitRegisteredVehicle = async (vehicleNumber) => {
        try {
            const selectedLocation = JSON.parse(localStorage.getItem("selectedLocation"));

            if (!selectedLocation || !selectedLocation.id || !selectedLocation.module?.module_name) {
                Swal.fire({
                    icon: "error",
                    title: "Location Not Selected",
                    text: "Please select a valid location before submitting.",
                });
                return;
            }

            const locationId = selectedLocation.id;
            const moduleName = selectedLocation.module.module_name;

            const payload = {
                Id: "TEST125",
                LprCameraName: "Gate1-Cam-A",
                PlateRead: vehicleNumber,
                timestamp: new Date().toISOString(),
                ContextImage: "https://via.placeholder.com/300x150.png?text=Truck+Image",
                sharpName: "Camera-Sharp",
            };

            const response = await axiosInstance.post(
                `/api/auth/manualEntry?module=${moduleName}&gateNumber=${locationId}`,
                payload
            );
            console.log("the response is", response);
            console.log("Vehicle registration response:", response.data);
            if (response.data.success) {
                Swal.fire({
                    icon: "success",
                    title: "Vehicle Submitted",
                    text: response.data.message || `Vehicle number ${vehicleNumber} submitted successfully`,
                });
                localStorage.setItem('manualEntryActive', 'false');
                setIsManual(false);
                setVehicleNumber("");
                setIsVehicleRegistered(false);  // reset
            } else {
                Swal.fire({
                    icon: "warning",
                    title: "Vehicle Not Processed",
                    text: response.data.message || "Vehicle not processed",
                });

            }
        } catch (error) {
            console.error("Error submitting registered vehicle:", error);
            Swal.fire({
                icon: "error",
                title: "Submission Failed",
                text: error.response?.data?.message || error.message,
            });
        }
    }

    //function to fetch the trip details accortding to the vendor code
    const fetchTripDetails = async (vendorCode) => {
        try {
            const response = await axiosInstance.get(`/api/scheduled/tripdetails/${vendorCode}`);
            console.log("the trip name is", response.data?.trip?.route_name);
            setRecommendedTrip(response.data?.trip);
            setRecommendedTripId(response.data?.trip_number)
        } catch (error) {
            console.error("Error fetching trip details:", error);
            setRecommendedTrip(null);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch trip details'
            });
        }
    }

    // ✅ Close modal safely and reset user flag
    const closeModal = () => {
        setShowFormModal(false);
        setOpenedByUser(false);
    };


    return (
        <div className="container-fluid p-4">
            <div className="row justify-content-center">
                {/* Left Side */}
                <div className="col-lg-7 col-md-12 mb-4">
                    <div className="card shadow-sm rounded p-4 text-center bg-white border position-relative">
                        {/* Source Badge */}
                        {vehicleData && (
                            <div
                                className="text-white px-3 py-1 rounded-pill shadow-sm source-badge"
                                style={{
                                    backgroundColor: getSourceColor(),
                                    position: "absolute",
                                    top: "10px",
                                    right: "10px",
                                    fontWeight: "bold",
                                    fontSize: "0.9rem",
                                    letterSpacing: "0.5px",
                                }}
                            >
                                {getSourceLabel()}
                            </div>
                        )}


                        {/* Destination Banner */}
                        {vehicleData && (
                            <div
                                className={`destination-banner text-white mb-2 rounded shadow-sm text-center
      ${["In-transit", "Bay Allocated"].includes(vehicleData.destinationStatus)
                                        ? "bg-success"
                                        : vehicleData.destinationStatus === "Parking"
                                            ? "bg-warning"
                                            : vehicleData.destinationStatus === "no_entry"
                                                ? "bg-danger"
                                                : ""
                                    }`}
                            >
                                <h3 className="m-0 fw-bold">
                                    {vehicleData.destinationStatus === "In-transit" && "🅿️ Going to Bay Area"}
                                    {vehicleData.destinationStatus === "Parking" && "🛑 Move to Parking Zone"}
                                    {vehicleData.destinationStatus === "Bay Allocated" && "🅿️ Proceed to Bay Area"}
                                    {vehicleData.destinationStatus === "no_entry" && "🚫 No Entry"}
                                </h3>
                            </div>
                        )}

                        {vehicleData?.ContextImage && vehicleData?.ContextImage !== "Not Available" ? (
                            // Case 1: Image available
                            <img
                                src={`data:image/png;base64,${vehicleData.ContextImage}`}
                                alt="Vehicle"
                                className={`img-fluid rounded border shadow-sm bg-light p-2 vehicle-image ${isNewVehicle ? "slide-in" : ""}`}
                            />
                        ) : vehicleData?.ContextImage === "Not Available" ? (
                            // Case 2: Image is "Not Available"
                            <div className="not-available-placeholder">
                                <div className="vehicle-image-placeholder">
                                    <p className="text-center">Image Not Available</p>
                                </div>
                            </div>
                        ) : !isManual ? (
                            // Case 3: Show scanning image only when IsManual is false
                            <div className="camera-scan-wrapper">
                                <div className="camera-frame">
                                    <div className="camera-scan-lines"></div>
                                    <img
                                        src="/Vehicle.jpg"
                                        alt="Scanning Vehicle"
                                        className="scanning-vehicle-image"
                                    />
                                    <div className="camera-laser"></div>
                                </div>
                                <div className="scan-status-text">
                                    <span className="dot-pulse"></span> ALPR Camera Scanning...
                                </div>
                            </div>
                        ) : null}



                        {/* Vehicle Number */}
                        <div className="mb-3 mt-4">
                            <h2 className={`vehicle-number-display ${isNewVehicle ? "bounce-in" : ""}`}>
                                {vehicleNumber ? formatVehicleNumber(vehicleNumber) : "--  --"}
                            </h2>
                        </div>
                        {vehicleData && remainingTime !== null && (
                            <div className="countdown-circle">
                                <div className="circle">
                                    <span>{remainingTime}</span>
                                </div>
                                <div className="countdown-label">Auto-reset in...</div>
                            </div>
                        )}

                        {/* Manual Entry UI */}
                        {isManual && (
                            <div className="mt-2">
                                <input
                                    type="text"
                                    className={`form-control text-center vehicle-no-input mb-2 ${showWarning ? "border-danger" : ""
                                        }`}
                                    placeholder="Enter Vehicle No."
                                    value={vehicleNumber}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setVehicleNumber(value);
                                        checkvehicleRegistration(value);

                                        // hide warning as soon as user types something
                                        if (value.trim() !== "") {
                                            setShowWarning(false);
                                        }
                                    }}
                                />

                                {/* Warning message */}
                                {showWarning && (
                                    <p className="text-danger small mb-2 text-center">
                                        Please enter the vehicle number.
                                    </p>
                                )}

                                <button
                                    className="btn gradient-bay px-4"
                                    onClick={() => {
                                        if (!vehicleNumber.trim()) {
                                            setShowWarning(true);
                                        } else {
                                            if (isVehicleRegistered) {
                                                handleSubmitRegisteredVehicle(vehicleNumber);
                                            } else {
                                                setShowFormModal(true);
                                            }
                                        }
                                    }}
                                >
                                    Submit
                                </button>
                            </div>
                        )}


                    </div>
                </div>

                {/* Right Side */}
                <div className="col-lg-5 col-md-12">
                    <div
                        className={`card p-4 shadow-sm bg-white border modern-card mb-4 ${isNewVehicle ? "fade-in-left" : ""
                            }`}
                    >
                        <h5 className="fw-bold border-bottom pb-2 text-center bg-primary text-white p-2 rounded">
                            🚪 Entry Details
                        </h5>
                        <p><strong>🕒 Entry Time:</strong> {vehicleData?.entryTime || "-- Not Available --"}</p>
                        <p><strong>🚪 Gate:</strong> {vehicleData?.gate || "-- Not Available --"}</p>
                        <p><strong>📆 Schedule Time:</strong> {vehicleData?.scheduleTime || "-- Not Available --"}</p>
                        <p><strong>🅿️ Bay Area:</strong> {vehicleData?.bayArea || "-- Not Available --"}</p>
                    </div>

                    <div
                        className={`card p-4 shadow-sm bg-white border modern-card ${isNewVehicle ? "fade-in-right" : ""
                            }`}
                    >
                        <h5 className="fw-bold border-bottom pb-2 text-center gradient-bay text-white p-2 rounded">
                            🚗 Driver Information
                        </h5>
                        <p><strong>👤 Owner:</strong> {vehicleData?.driver?.owner || "-- Not Available --"}</p>
                        <p><strong>📞 Contact:</strong> {vehicleData?.driver?.contact || "-- Not Available --"}</p>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-4">
                        <button
                            className="btn btn-dark py-2 px-4"
                            onClick={() => setSource("rfid")}
                        >
                            RFID
                        </button>

                        <button
                            className="btn gradient-bay px-4 py-2"
                            onClick={() => {
                                setOpenedByUser(true);
                                // setShowFormModal(true);
                                handleManualToggle();
                                setVehicleData(null);
                                setSource("manual");
                            }}
                        >
                            Manual Entry
                        </button>
                    </div>

                </div>
                {showFormModal && !isVehicleRegistered && (
                    <div className="modal-overlay">
                        <div className="modal-content">
                            <h5 className="fw-bold text-center mb-3">📝 Schedule Vehicle Entry</h5>

                            <div className="container">
                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <label> Vehicle Number</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={formatVehicleNumber(vehicleNumber)}
                                            disabled
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label>Date</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={new Date().toISOString().split("T")[0]}
                                            disabled
                                        />
                                    </div>
                                </div>

                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <label htmlFor="vendorCode" className="form-label">
                                            Vendor Code
                                        </label>
                                        <div className="position-relative" ref={vendorListRef}>
                                            <input
                                                type="text"
                                                className="form-control mb-2"
                                                placeholder="Search vendors..."
                                                value={vendorSearch}
                                                onChange={handleVendorSearch}
                                                onFocus={() => setIsVendorDropdownOpen(true)}
                                            />
                                            {isVendorDropdownOpen && (
                                                <div
                                                    className="form-select vendor-list-container"
                                                    style={{
                                                        maxHeight: '200px',
                                                        overflowY: 'auto',
                                                        position: 'absolute',
                                                        width: '100%',
                                                        zIndex: 1000,
                                                        backgroundColor: 'white',
                                                        border: '1px solid #ced4da',
                                                        borderRadius: '0.25rem',
                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                                    }}
                                                    onScroll={handleVendorScroll}
                                                >
                                                    {vendorList.map((vendor) => (
                                                        <div
                                                            key={vendor.id}
                                                            className="vendor-option p-2 cursor-pointer hover-bg-light"
                                                            onClick={() => {
                                                                setVendorCode(vendor.vendor_code);
                                                                setVendorSearch(vendor.vendor_name);
                                                                setIsVendorDropdownOpen(false);

                                                            }}
                                                        >
                                                            {vendor.vendor_code} - {vendor.vendor_name}
                                                        </div>
                                                    ))}
                                                    {loadingVendors && (
                                                        <div className="text-center p-2">
                                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                                            Loading...
                                                        </div>
                                                    )}
                                                    {!hasMore && vendorList.length > 0 && (
                                                        <div className="text-center p-2 text-muted">
                                                            No more vendors
                                                        </div>
                                                    )}
                                                    {!loadingVendors && vendorList.length === 0 && (
                                                        <div className="text-center p-2 text-muted">
                                                            No vendors found
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {/* <div className="col-md-6">
                                        <label>🧾 Trip Id</label>
                                        <div className="position-relative" ref={tripListRef}>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Recommended Trip..."
                                                value={recommendedTrip?.route_name || ''}
                                                readOnly
                                                style={{ backgroundColor: '#f8f9fa' }}
                                            />
                                        </div>
                                    </div> */}
                                </div>

                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <label>👤 Driver Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter driver name"
                                            value={driverName}
                                            onChange={(e) => setDriverName(e.target.value)}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label>📞 Driver Contact</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter driver contact"
                                            value={driverContact}
                                            onChange={(e) => setDriverContact(e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div className="d-flex justify-content-end gap-2">
                                    <button className="btn btn-secondary" onClick={() => closeModal()}>Cancel</button>
                                    <button className="btn btn-success" onClick={handleSubmit}>Submit</button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default VehicleEntry;